import { Helmet } from 'react-helmet-async';
import { DEFAULT_SITE_META_DESCRIPTION, getSiteUrl, resolveCanonicalUrl } from '../constants/seo';

/**
 * SEO: meta tags, Open Graph, Twitter Cards, JSON-LD.
 * Canonical URLs always use REACT_APP_SITE_URL (default https://www.ewio.org) + pathname — no query/hash.
 * Google does not use the keywords meta tag; it is omitted.
 *
 * @param {string} [props.url] - Optional full URL or path; normalized to site canonical
 * @param {string} [props.pathnameFallback] - When `url` is omitted and `window` is missing (tests)
 */
export default function SEO({
  title = 'EWIO – Peer‑to‑Peer Skill‑Based Crypto Betting Platform',
  description = DEFAULT_SITE_META_DESCRIPTION,
  image = '/og-image.png',
  imageAlt = 'EWIO — peer-to-peer skill-based crypto betting',
  url,
  pathnameFallback = '/',
  type = 'website',
  structuredData,
  robots = 'index, follow',
}) {
  const siteUrl = getSiteUrl();
  const canonicalUrl = resolveCanonicalUrl(url, pathnameFallback);
  const absoluteImage = image.startsWith('http') ? image : `${siteUrl}${image.startsWith('/') ? image : `/${image}`}`;

  const dataArray = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <meta name="language" content="English" />
      <meta name="author" content="EWIO" />

      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}

      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImage} />
      {imageAlt ? <meta property="og:image:alt" content={imageAlt} /> : null}
      <meta property="og:site_name" content="EWIO" />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />
      {imageAlt ? <meta name="twitter:image:alt" content={imageAlt} /> : null}
      <meta name="twitter:creator" content="@IOGaming" />
      <meta name="twitter:site" content="@IOGaming" />

      <meta name="theme-color" content="#000000" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="EWIO" />

      <link rel="alternate" type="application/rss+xml" title="EWIO Blog (RSS)" href="/rss.xml" />
      <link rel="alternate" type="application/atom+xml" title="EWIO Blog (Atom)" href="/feed.xml" />

      {dataArray.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}
